import { ref } from 'vue'
import type { Ref } from 'vue'
import localforage from 'localforage'

import { useStore } from '@web/store'
import { APP_ID } from '@web/common/config'
import { superSubscriptionSegment } from '@web/common/ExternalConfig/getters'
import { isFirstCourseDay } from '@web/common/course/firstCourseDay'
import { isEmbedded } from '@web/composition/useEmbedMode'
import { QUEUE_PRIORITY, modalQueue } from '@web/platform.v3/modal-builder/modalQueue'

export type ModalVariant = 'continue_in_app' | 'setup_reminders'

// pending — «первый день завершён, модалку надо показать при уходе со страницы / входе в ЛК».
// shown — «модалка уже показана (или исчерпана), больше не показываем никогда».
export const POST_FIRST_DAY_PENDING_KEY = `post_first_day_modal_pending_${APP_ID}`
export const POST_FIRST_DAY_SHOWN_KEY = `post_first_day_modal_shown_${APP_ID}`

// MOB-547: DAY-1 и модалки конструктора делят одну очередь — серверная идёт первой.
export const DAY1_QUEUE_ID = 'post-first-day'

interface VariantDeps {
  hasSuper: boolean
  notificationsEnabled: boolean
}

/**
 * Вариант модалки в момент ПОКАЗА (первый день уже подтверждён pending-флагом).
 * null = показывать нечего (нет SUPER и уведомления уже включены).
 */
export function resolveVariantNow ({ hasSuper, notificationsEnabled }: VariantDeps): ModalVariant | null {
  if (hasSuper) return 'continue_in_app'
  if (!notificationsEnabled) return 'setup_reminders'
  return null
}

// Синглтон-шина: один хост в дереве
const isOpen = ref(false)
const variant: Ref<ModalVariant | null> = ref(null)

export function usePostFirstDayModal () {
  const store = useStore()

  function hasSuperSubscription (): boolean {
    const segments = store.getters['user/user']?.segments ?? []
    const superId = superSubscriptionSegment()
    return segments.some((s: { id: number }) => s.id === superId)
  }

  /**
   * Завершение первого дня курса — фиксируем намерение показать модалку (pending).
   * Вариант здесь НЕ считаем: сегмент/уведомления определяются в момент показа.
   */
  async function markPending (input: { day: Day; courseId?: number }): Promise<void> {
    if (!input.day || input.courseId == null) return

    // Уже показывали или уже запланировано — второй раз не планируем.
    const [alreadyShown, alreadyPending] = await Promise.all([
      localforage.getItem(POST_FIRST_DAY_SHOWN_KEY).catch(() => null),
      localforage.getItem(POST_FIRST_DAY_PENDING_KEY).catch(() => null)
    ])
    if (alreadyShown || alreadyPending) return

    const days: Day[] = store.getters['day/getDaysByCourseId'](input.courseId) ?? []
    if (!isFirstCourseDay(input.day, days)) return

    await localforage.setItem(POST_FIRST_DAY_PENDING_KEY, true).catch(() => undefined)
  }

  /**
   * Показать модалку, если есть pending-флаг (уход со страницы дня / вход в ЛК).
   * Вариант считаем сейчас по актуальному сегменту/уведомлениям. В любом исходе
   * (показали или вариант отпал) pending исчерпывается и ставится shown — правило «один раз».
   * Если модалка ждёт в очереди, флаги ставятся только когда она реально откроется.
   */
  async function showIfPending (): Promise<void> {
    // Во встроенном режиме (MOB-534) не показываем: оба варианта уводят из
    // сценария — «продолжить в приложении» пользователю, который уже в нём,
    // и настройки напоминаний. pending не трогаем: модалка дождётся браузера.
    if (isEmbedded()) return

    const alreadyShown = await localforage.getItem(POST_FIRST_DAY_SHOWN_KEY).catch(() => null)
    if (alreadyShown) return

    const pending = await localforage.getItem(POST_FIRST_DAY_PENDING_KEY).catch(() => null)
    if (!pending) return

    const resolved = resolveVariantNow({
      hasSuper: hasSuperSubscription(),
      notificationsEnabled: Boolean(store.getters['user/user']?.enabledScheduleNotifications)
    })
    // Показывать нечего — pending исчерпан, больше не пытаемся.
    if (!resolved) {
      await exhaustPending()
      return
    }

    // MOB-547: флаги — в момент реального открытия, а не постановки в очередь.
    // Иначе закрыли вкладку, пока DAY-1 ждала за серверной модалкой, — и она потеряна.
    modalQueue.enqueue({
      id: DAY1_QUEUE_ID,
      priority: QUEUE_PRIORITY.day1,
      open: () => {
        exhaustPending()
        variant.value = resolved
        isOpen.value = true
      }
    })
  }

  // shown — раньше pending: оборвись запись посередине, модалка не покажется второй раз.
  async function exhaustPending (): Promise<void> {
    await localforage.setItem(POST_FIRST_DAY_SHOWN_KEY, true).catch(() => undefined)
    await localforage.removeItem(POST_FIRST_DAY_PENDING_KEY).catch(() => undefined)
  }

  function close (): void {
    isOpen.value = false
    variant.value = null
    modalQueue.release(DAY1_QUEUE_ID)
  }

  return { isOpen, variant, markPending, showIfPending, close }
}
